import { get, post, put, del } from './http'
import { setActingTenantId, setMe } from './session'
import type {
  AppCard,
  AppGrant,
  AppInfo,
  AppPermTree,
  AuditLog,
  AuditQuery,
  CaptchaResp,
  ChangePasswordInput,
  CreateTenantInput,
  CreateTenantResult,
  CreateUserInput,
  CreateUserResult,
  Dept,
  DeptInput,
  LoginInput,
  MeView,
  MyPerms,
  PageResult,
  Role,
  RoleInput,
  RoleRow,
  Tenant,
  TenantQuery,
  TenantRow,
  TenantView,
  UpdateTenantInput,
  UserInput,
  UserQuery,
  UserRow,
} from './types'

export const login = (input: LoginInput) => post<MeView>('/login', input).then(setMe)

export const captcha = () => get<CaptchaResp>('/captcha')

export const logout = () => post<void>('/session/logout')

export const me = () => get<MeView>('/me').then(setMe)

export const changePassword = (input: ChangePasswordInput) =>
  post<MeView>('/me/password', input).then(setMe)

export const myApps = () => get<AppCard[]>('/me/apps')

export const myPerms = (app: string) => get<MyPerms>('/me/perms', { app })

export const tenantOptions = () => get<TenantView[]>('/me/tenant-options')

export const actingTenant = (tenantId: number) =>
  post<TenantView | undefined>('/me/acting-tenant', { tenant_id: tenantId }).then((t) => {
    setActingTenantId(tenantId)
    return t
  })

export const listApps = () => get<AppInfo[]>('/admin/apps')

export const listTenants = (q: TenantQuery) => get<PageResult<TenantRow>>('/admin/tenants', { ...q })

export const createTenant = (input: CreateTenantInput) =>
  post<CreateTenantResult>('/admin/tenants', input)

export const updateTenant = (id: number, input: UpdateTenantInput) =>
  put<Tenant>(`/admin/tenants/${id}`, input)

export const setTenantApps = (id: number, apps: AppGrant[]) =>
  put<void>(`/admin/tenants/${id}/apps`, { apps })

export const listDepts = () => get<Dept[]>('/admin/depts')

export const createDept = (input: DeptInput) => post<Dept>('/admin/depts', input)

export const updateDept = (id: number, input: DeptInput) => put<Dept>(`/admin/depts/${id}`, input)

export const deleteDept = (id: number) => del<void>(`/admin/depts/${id}`)

export const listUsers = (q: UserQuery) => get<PageResult<UserRow>>('/admin/users', { ...q })

export const createUser = (input: CreateUserInput) => post<CreateUserResult>('/admin/users', input)

export const updateUser = (id: number, input: UserInput) => put<void>(`/admin/users/${id}`, input)

export const resetPassword = (id: number) =>
  post<{ initial_password: string }>(`/admin/users/${id}/reset-password`)

export const setUserStatus = (id: number, status: 'active' | 'disabled') =>
  post<void>(`/admin/users/${id}/status`, { status })

export const listRoles = () => get<RoleRow[]>('/admin/roles')

export const createRole = (input: RoleInput) => post<Role>('/admin/roles', input)

export const updateRole = (id: number, input: RoleInput) => put<Role>(`/admin/roles/${id}`, input)

export const deleteRole = (id: number) => del<void>(`/admin/roles/${id}`)

export const roleGrants = (id: number) => get<string[]>(`/admin/roles/${id}/permissions`)

export const setRoleGrants = (id: number, codes: string[]) =>
  put<void>(`/admin/roles/${id}/permissions`, { codes })

export const permissionTree = () => get<AppPermTree[]>('/admin/permission-tree')

export const listAuditLogs = (q: AuditQuery) => get<PageResult<AuditLog>>('/admin/audit-logs', { ...q })